import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { useJobs } from "../../hooks/useJobs";
import { JobApiItem } from "../../types";
import CareersForm from "./CareersForm";
import Footer from "./Footer";

export const DetailCarrers: React.FC = () => {
  const { getJobsDetails } = useJobs();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobApiItem | null>(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams<{ id: string }>();
  const applicationFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchJob = async () => {
      setLoading(true);
      const job = await getJobsDetails(id);
      setJob(job);
      setLoading(false);
    };
    fetchJob();
  }, [id]);

  const handleApply = () => {
    if (applicationFormRef.current) {
      applicationFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div className="min-h-screen w-full bg-black text-white">
      {loading ? (
        <div className="flex items-center justify-center h-[60vh] text-white/70">
          Cargando...
        </div>
      ) : (
        job && (
          <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-24">
            <div className="pt-28 text-center">
              <h6 className="uppercase tracking-wide text-sm text-white">
                Empleos
              </h6>
              <h1 className="mt-6 text-3xl md:text-4xl font-bold leading-[1.05]">
                {job.jobTitle}
              </h1>
            </div>

            {/* Sobre el rol */}
            <section className="mt-16">
              <h3 className="font-bold text-base text-white mb-2 text-left">
                Sobre el Rol
              </h3>
              <div className="max-w-none text-left">
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  remarkPlugins={[remarkGfm]}
                  components={{
                    ul: ({ node, ordered, ...props }) => (
                      <ul className="list-disc pl-6 space-y-1" {...props} />
                    ),
                    ol: ({ node, ordered, ...props }) => (
                      <ol className="list-decimal pl-6 space-y-1" {...props} />
                    ),
                    li: ({ node, ordered, ...props }) => (
                      <li className="leading-relaxed" {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="mb-4" {...props} />
                    ),
                  }}
                >
                  {job.roleDescription}
                </ReactMarkdown>
              </div>
            </section>

            {/* Responsabilidades */}
            <section className="mt-12">
              <h3 className="font-bold text-base text-white mb-2 text-left">
                Responsabilidades
              </h3>
              <div className="max-w-none text-left">
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  remarkPlugins={[remarkGfm]}
                  components={{
                    ul: ({ node, ordered, ...props }) => (
                      <ul className="list-disc pl-6 space-y-1" {...props} />
                    ),
                    ol: ({ node, ordered, ...props }) => (
                      <ol className="list-decimal pl-6 space-y-1" {...props} />
                    ),
                    li: ({ node, ordered, ...props }) => (
                      <li className="leading-relaxed" {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="mb-4" {...props} />
                    ),
                  }}
                >
                  {job.jobResponsibilities}
                </ReactMarkdown>
              </div>
            </section>

            {/* Sobre ti */}
            <section className="mt-12">
              <h3 className="font-bold text-base text-white mb-2 text-left">
                Sobre ti
              </h3>
              <div className="max-w-none text-left">
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  remarkPlugins={[remarkGfm]}
                  components={{
                    ul: ({ node, ordered, ...props }) => (
                      <ul className="list-disc pl-6 space-y-1" {...props} />
                    ),
                    ol: ({ node, ordered, ...props }) => (
                      <ol className="list-decimal pl-6 space-y-1" {...props} />
                    ),
                    li: ({ node, ordered, ...props }) => (
                      <li className="leading-relaxed" {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="mb-4" {...props} />
                    ),
                  }}
                >
                  {job.jobRequirements}
                </ReactMarkdown>
              </div>
            </section>

            {/* Beneficios */}
            <section className="mt-12">
              <h3 className="font-bold text-base text-white mb-2 text-left">
                Algunos de nuestros beneficios
              </h3>
              <div className="max-w-none text-left">
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  remarkPlugins={[remarkGfm]}
                  components={{
                    ul: ({ node, ordered, ...props }) => (
                      <ul className="list-disc pl-6 space-y-1" {...props} />
                    ),
                    ol: ({ node, ordered, ...props }) => (
                      <ol className="list-decimal pl-6 space-y-1" {...props} />
                    ),
                    li: ({ node, ordered, ...props }) => (
                      <li className="leading-relaxed" {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="mb-4" {...props} />
                    ),
                  }}
                >
                  {job.jobBenefits}
                </ReactMarkdown>
              </div>
            </section>

            <section className="mt-12">
              <h3 className="font-bold text-base text-white mb-2 text-left">
                Sobre nosotros
              </h3>

              <p className="text-base text-left">
                Somos la empresa líder en la creación de propuestas de valor que
                generan energía y dinamismo en los mercados y la sociedad.
                Nuestro talento comprometido en la generación de soluciones
                inusuales y de alto impacto inspira un cambio en las personas y
                genera valor para nuestros clientes. ¡Únete a nosotros y
                descubre cómo podemos marcar la diferencia juntos!
              </p>
            </section>

            {/* Botón aplicar */}
            <div className="flex items-center justify-between py-16 gap-4">
              <button
                className="w-48 h-10 bg-transparent border border-solid border-white text-white font-inter font-semibold text-sm leading-5 tracking-wider transition-colors duration-200 cursor-pointer hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                onClick={() => navigate("/careers")}
              >
                Volver
              </button>

              <button
                className="w-48 h-10 bg-white text-black font-inter font-semibold text-sm leading-5 tracking-wider transition-colors duration-200 cursor-pointer hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                onClick={handleApply}
              >
                Aplicar
              </button>
            </div>
          </div>
        )
      )}
      {/* Sección del formulario de aplicación */}
      <div ref={applicationFormRef} className="bg-white text-black py-16">
        <div className="w-full max-w-5xl mx-auto px-6 md:px-10 lg:px-24">
          <div className="w-full flex justify-between items-center text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold">
              Aplica a este puesto
            </h2>
            <p className="text-sm text-gray-600 text-right">
              <span className="text-red-500">*</span> Obligatorio
            </p>
          </div>
          <CareersForm />
        </div>
      </div>
      <Footer isSimple={true} />
    </div>
  );
};

export default DetailCarrers;
